import React, { useState } from 'react';

import { TranscriptBlockQuote } from './TranscriptBlockQuote';
import { useGetTranscriptSubfact } from '../../../../api/queries/useGetTranscripts';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';

interface SubfactReferenceProps {
  subfactId: string;
  referenceNumber: number;
  caseId?: string;
  transcriptId?: string;
  onBlockClick?: (blockId: string) => void;
  isRecordingComplete?: boolean;
}

interface SubfactContentProps {
  subfactId: string;
  caseId?: string;
  transcriptId?: string;
  onBlockClick?: (blockId: string) => void;
  isRecordingComplete?: boolean;
  onClose: () => void;
}

const SubfactContent: React.FC<SubfactContentProps> = ({
  subfactId,
  caseId,
  transcriptId,
  onBlockClick,
  isRecordingComplete = true,
  onClose,
}) => {
  // Only fetch the subfact when this component is rendered (when popover is open)
  const { data: subfact, isLoading } = useGetTranscriptSubfact(caseId || null, transcriptId || null, subfactId);

  const handleBlockClick = (blockId: string) => {
    onClose(); // Close the popover
    onBlockClick?.(blockId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="text-xs text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!subfact) {
    return <div className="text-xs text-gray-500">No subfact found</div>;
  }

  return (
    <div className="space-y-2">
      {subfact.referencedBlocks && subfact.referencedBlocks.length > 0 ? (
        subfact.referencedBlocks.map((block) => {
          return (
            <TranscriptBlockQuote
              key={block.id}
              block={block}
              caseId={caseId}
              transcriptId={transcriptId}
              isRecordingComplete={isRecordingComplete}
              onClick={() => handleBlockClick(block.id)}
            />
          );
        })
      ) : (
        <div className="text-xs text-gray-500">No transcript blocks found</div>
      )}
    </div>
  );
};

export const SubfactReference: React.FC<SubfactReferenceProps> = ({
  subfactId,
  referenceNumber,
  caseId,
  transcriptId,
  onBlockClick,
  isRecordingComplete = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mx-0.5 inline-flex cursor-pointer items-center justify-center rounded border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-600 transition-colors duration-150 hover:border-blue-300 hover:bg-blue-100 hover:text-blue-800"
          style={{ display: 'inline-flex', verticalAlign: 'baseline' }}
        >
          {referenceNumber}
        </button>
      </PopoverTrigger>

      <PopoverContent className="max-h-96 w-96 overflow-y-auto p-2" side="top" align="center">
        {isOpen && (
          <SubfactContent
            subfactId={subfactId}
            caseId={caseId}
            transcriptId={transcriptId}
            onBlockClick={onBlockClick}
            isRecordingComplete={isRecordingComplete}
            onClose={() => setIsOpen(false)}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
